import { createContext, ReactNode, useContext, useMemo } from 'react';

/** Matches the z-index every drawer hardcoded before the manager owned it. */
const DRAWER_BASE_Z_INDEX = 1001;

/**
 * Wide enough that a stacked drawer clears any popup (Select, DatePicker,
 * Popconfirm) opened by the drawer beneath it — Ant Design offsets those from
 * their own drawer's z-index.
 */
const DRAWER_Z_INDEX_STEP = 100;

const DrawerStackContext = createContext<number>(DRAWER_BASE_Z_INDEX);

const drawerZIndexForDepth = (depth: number) =>
  DRAWER_BASE_Z_INDEX + depth * DRAWER_Z_INDEX_STEP;

interface DrawerStackProviderProps {
  depth: number;
  children: ReactNode;
}

/**
 * Gives everything rendered inside one stack entry its layer. Drawers do not
 * read this directly — `DrawerWithButtons` picks it up so a converted drawer
 * needs no z-index handling of its own.
 */
const DrawerStackProvider = ({ depth, children }: DrawerStackProviderProps) => {
  const zIndex = useMemo(() => drawerZIndexForDepth(depth), [depth]);

  return (
    <DrawerStackContext.Provider value={zIndex}>
      {children}
    </DrawerStackContext.Provider>
  );
};

/**
 * The z-index for the current stack entry. Outside the manager it resolves to
 * the historical 1001, so unconverted drawers keep rendering exactly as before.
 */
const useDrawerZIndex = () => useContext(DrawerStackContext);

export {
  DRAWER_BASE_Z_INDEX,
  DRAWER_Z_INDEX_STEP,
  DrawerStackProvider,
  drawerZIndexForDepth,
  useDrawerZIndex,
};
